import {
  Configuration,
  ConflictLevelsStatusEnum,
  CreateTaskDto,
  PaginatedTasksDto,
  Task,
  TaskFilterOptionsDto,
  TaskTypeEnum,
  TasksApi,
  UpdateTaskDto,
} from '@sr-sdks/api-sdk-axios';
import {
  QueryClient,
  keepPreviousData,
  queryOptions,
  skipToken,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type { RawAxiosRequestConfig } from 'axios';
import { configuration } from '../../configuration';
import axiosInstance, { bypassCacheConfig } from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';

export const TASKS_DEFAULT_PARAMS = {
  page: 1,
  pageSize: 25,
  sortBy: 'created:asc',
} as const satisfies TasksPaginatedParams;

const tasksQueryKeys = {
  filterOptions: (params?: Partial<TaskFilterOptionsParams>) =>
    ['tasks', 'filterOptions', { ...params }] as const,
  paginated: (params?: TasksPaginatedParams) =>
    ['tasks', 'paginated', { ...params }] as const,
  single: (taskId?: string) => ['tasks', 'single', { taskId }] as const,
};

export interface TasksPaginatedParams {
  clientIds?: string[];
  competingClaimId?: string;
  dueDates?: string[];
  /** Competing claims only: also list completed tasks badged Resolved. */
  includeResolved?: boolean;
  otherClaimants?: string[];
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  sortBy?: string;
  statuses?: ConflictLevelsStatusEnum[];
  /** Filters to tasks whose titleId is in this list. Added in APP-2180 B2. */
  titleIds?: string[];
  type?: TaskTypeEnum;
  workspaceId?: string;
}

export interface TaskFilterOptionsParams {
  /** Competing claims only: also draw on completed tasks badged Resolved. */
  includeResolved?: boolean;
  type: TaskTypeEnum;
  workspaceId: string;
}

export interface CreateTaskParams {
  createTaskDto: CreateTaskDto;
}

export interface UpdateTaskParams {
  id: string;
  updateTaskDto: UpdateTaskDto;
}

// Get Tasks API instance
const GetTasksApi = () => {
  return new TasksApi(
    new Configuration(),
    configuration.TASKS_API_BASE,
    axiosInstance,
  );
};

// Fetch tasks with pagination
export const fetchTasksPaginated = async (
  params: TasksPaginatedParams,
  options?: RawAxiosRequestConfig,
): Promise<PaginatedTasksDto> => {
  try {
    const response = await GetTasksApi().taskControllerFindAll(
      params?.clientIds,
      params?.competingClaimId,
      params?.dueDates,
      params?.includeResolved,
      params?.otherClaimants,
      params?.searchTerm,
      params?.statuses,
      params?.titleIds,
      params?.type,
      params?.workspaceId,
      params?.page,
      params?.pageSize,
      params?.sortBy,
      { ...bypassCacheConfig(), ...options },
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'taskControllerFindAll',
      params: params,
    });
  }
};

// Fetch the distinct filter values for one tab's open tasks
export const fetchTaskFilterOptions = async (
  { includeResolved, type, workspaceId }: TaskFilterOptionsParams,
  options?: RawAxiosRequestConfig,
): Promise<TaskFilterOptionsDto> => {
  try {
    const response = await GetTasksApi().taskControllerFindFilterOptions(
      type,
      workspaceId,
      includeResolved,
      { ...bypassCacheConfig(), ...options },
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'taskControllerFindFilterOptions',
      params: { type, workspaceId },
    });
  }
};

// Fetch a single task by ID
export const fetchTaskById = async (id: string): Promise<Task> => {
  try {
    const response = await GetTasksApi().taskControllerFindOne(
      id,
      bypassCacheConfig(),
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'taskControllerFindOne',
      params: { id },
    });
  }
};

// Create a new task
export const createTask = async ({
  createTaskDto,
}: CreateTaskParams): Promise<Task> => {
  try {
    const response = await GetTasksApi().taskControllerCreate(createTaskDto);

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'taskControllerCreate',
      params: createTaskDto,
    });
  }
};

// Update an existing task
export const updateTask = async ({
  id,
  updateTaskDto,
}: UpdateTaskParams): Promise<Task> => {
  try {
    const response = await GetTasksApi().taskControllerUpdate(
      id,
      updateTaskDto,
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'taskControllerUpdate',
      params: { id, updateTaskDto },
    });
  }
};

// Delete a task by ID
export const deleteTask = async (id: string): Promise<Task> => {
  try {
    const response = await GetTasksApi().taskControllerRemove(id);

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'taskControllerRemove',
      params: { id },
    });
  }
};

const selectTasksPaginated = (data: PaginatedTasksDto): PaginatedTasksDto => {
  return {
    ...data,
    items: data.items.filter((v) => Boolean(v._id)),
  };
};

export const queryOptionsGetTasksPaginated = (
  params?: TasksPaginatedParams,
) => {
  params = {
    ...TASKS_DEFAULT_PARAMS,
    ...params,
  };

  return queryOptions({
    placeholderData: keepPreviousData,
    queryFn: params ? () => fetchTasksPaginated(params) : skipToken,
    queryKey: tasksQueryKeys.paginated(params),
    select: selectTasksPaginated,
  });
};

export const queryOptionsGetTaskFilterOptions = (
  params: Partial<TaskFilterOptionsParams>,
) => {
  const { includeResolved, type, workspaceId } = params;

  return queryOptions({
    queryFn:
      type && workspaceId
        ? () => fetchTaskFilterOptions({ includeResolved, type, workspaceId })
        : skipToken,
    // Left out when unset, so every open-only reader keeps sharing one entry.
    queryKey: tasksQueryKeys.filterOptions({
      type,
      workspaceId,
      ...(includeResolved && { includeResolved }),
    }),
    // Home, Title summary and each Tasks tab read these (cards name the other
    // claimant from them), and every refetch fans out to the workspaces API.
    // Filter values change rarely, so a minute's reuse loses little.
    staleTime: 60_000,
  });
};

export const queryOptionsGetTask = (taskId?: string) => {
  return queryOptions({
    queryFn: taskId ? () => fetchTaskById(taskId) : skipToken,
    queryKey: tasksQueryKeys.single(taskId),
  });
};

// Hook for creating a new task
export const useCreateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      // Invalidate paginated queries
      queryClient.invalidateQueries({
        queryKey: tasksQueryKeys.paginated(),
      });
    },
  });
};

// Hook for updating a task
export const useUpdateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTask,
    onSuccess: (data, variables) => {
      // Update the specific task in cache
      queryClient.setQueryData(tasksQueryKeys.single(variables.id), data);

      // Invalidate paginated queries
      queryClient.invalidateQueries({
        queryKey: tasksQueryKeys.paginated(),
      });
    },
  });
};

// Hook for deleting a task
export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTask,
    onSuccess: (data, id) => {
      // Remove the task from cache
      queryClient.removeQueries({
        queryKey: tasksQueryKeys.single(id),
      });

      // Invalidate paginated queries
      queryClient.invalidateQueries({
        queryKey: tasksQueryKeys.paginated(),
      });
    },
  });
};

/**
 * Refetches every task list and the tabs' filter options (cards name the other
 * claimant from them), for a change made outside the Tasks pages (e.g.
 * deleting a title).
 */
export const invalidateTaskLists = async (queryClient: QueryClient) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: tasksQueryKeys.paginated() }),
    queryClient.invalidateQueries({ queryKey: tasksQueryKeys.filterOptions() }),
  ]);
};

/**
 * Drops a task from every cached task list without refetching. For a task the
 * backend removes asynchronously (a draft's task is deleted by an event handler
 * after its title), where an immediate refetch could still return it.
 */
export const removeTaskFromCachedLists = (
  queryClient: QueryClient,
  taskId: string,
) => {
  queryClient.setQueriesData<PaginatedTasksDto>(
    { queryKey: tasksQueryKeys.paginated() },
    (data) => {
      if (!data?.items.some((item) => item._id === taskId)) return data;

      return {
        ...data,
        items: data.items.filter((item) => item._id !== taskId),
        meta: {
          ...data.meta,
          itemCount: Math.max(0, data.meta.itemCount - 1),
          totalItems: Math.max(0, data.meta.totalItems - 1),
        },
      };
    },
  );

  queryClient.removeQueries({ queryKey: tasksQueryKeys.single(taskId) });
};
